import { createContext, useReducer, useCallback } from "react";

import axios from "../utils/axios";
import Tr069DeviceReducer from "../reducers/Tr069DeviceReducer";
import { Tr069DeviceConstants } from "../constants/tr069device.constants";

const initialState = {
  isLoading: false,
  isLoadingTasks: false,
  isSubmitting: false,
  hasError: false,
  device: null,
  tasks: [],
};

const Tr069DeviceContext = createContext({
  ...initialState,
  getDeviceByRadUser: () => {},
  linkDevice: () => {},
  unlinkDevice: () => {},
  getDeviceTasks: () => {},
  refreshParameters: () => {},
  rebootDevice: () => {},
  factoryResetDevice: () => {},
});

function Tr069DeviceProvider({ children }) {
  const [state, dispatch] = useReducer(Tr069DeviceReducer, initialState);

  const getDeviceByRadUser = useCallback(async (radUserId) => {
    try {
      dispatch({ type: Tr069DeviceConstants.GET_DEVICE_REQUEST });
      const response = await axios.get(`/tr069/devices/by-rad-user/${radUserId}`);
      dispatch({
        type: Tr069DeviceConstants.GET_DEVICE_SUCCESS,
        payload: response.data || null,
      });
      return response.data || null;
    } catch (exception) {
      dispatch({ type: Tr069DeviceConstants.GET_DEVICE_FAILURE });
    }
  }, []);

  const linkDevice = useCallback(async (radUserId, serialNumber) => {
    dispatch({ type: Tr069DeviceConstants.LINK_DEVICE_REQUEST });
    try {
      const response = await axios.post(`/tr069/devices/by-rad-user/${radUserId}/link`, {
        serialNumber,
      });
      dispatch({
        type: Tr069DeviceConstants.LINK_DEVICE_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } catch (exception) {
      dispatch({ type: Tr069DeviceConstants.LINK_DEVICE_FAILURE });
      throw exception;
    }
  }, []);

  const unlinkDevice = useCallback(async (deviceId) => {
    dispatch({ type: Tr069DeviceConstants.UNLINK_DEVICE_REQUEST });
    try {
      await axios.post(`/tr069/devices/${deviceId}/unlink`);
      dispatch({ type: Tr069DeviceConstants.UNLINK_DEVICE_SUCCESS });
    } catch (exception) {
      dispatch({ type: Tr069DeviceConstants.UNLINK_DEVICE_FAILURE });
      throw exception;
    }
  }, []);

  const getDeviceTasks = useCallback(async (deviceId) => {
    dispatch({ type: Tr069DeviceConstants.GET_DEVICE_TASKS_REQUEST });
    try {
      const response = await axios.get(`/tr069/devices/${deviceId}/tasks`);
      dispatch({
        type: Tr069DeviceConstants.GET_DEVICE_TASKS_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } catch (exception) {
      dispatch({ type: Tr069DeviceConstants.GET_DEVICE_TASKS_FAILURE });
    }
  }, []);

  const createTask = async (url, body) => {
    dispatch({ type: Tr069DeviceConstants.CREATE_TASK_REQUEST });
    try {
      const response = await axios.post(url, body);
      dispatch({ type: Tr069DeviceConstants.CREATE_TASK_SUCCESS });
      return response.data;
    } catch (exception) {
      dispatch({ type: Tr069DeviceConstants.CREATE_TASK_FAILURE });
      throw exception;
    }
  };

  const refreshParameters = useCallback(
    (deviceId, parameterNames = []) =>
      createTask(`/tr069/devices/${deviceId}/parameters/refresh`, parameterNames),
    [],
  );

  const rebootDevice = useCallback(
    (deviceId) => createTask(`/tr069/devices/${deviceId}/reboot`),
    [],
  );

  const factoryResetDevice = useCallback(
    (deviceId) => createTask(`/tr069/devices/${deviceId}/factory-reset`),
    [],
  );

  return (
    <Tr069DeviceContext.Provider
      value={{
        ...state,
        getDeviceByRadUser,
        linkDevice,
        unlinkDevice,
        getDeviceTasks,
        refreshParameters,
        rebootDevice,
        factoryResetDevice,
      }}
    >
      {children}
    </Tr069DeviceContext.Provider>
  );
}

export { Tr069DeviceContext, Tr069DeviceProvider };
